import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { avaliarAcao, type AcaoDaMensagem, type MensagemDto } from '@crm/shared'
import {
  IconeCopiar,
  IconeLapis,
  IconeLixeira,
  IconeResponder,
  IconeSetaBaixo,
} from '../../componentes/icones'
import { juntar } from '../../componentes/ui'

/**
 * A setinha no canto da mensagem e o menu que ela abre (ADR 0026).
 *
 * As regras de quem pode o quê, e até quando, moram em `avaliarAcao`, no
 * pacote comum, e são as mesmas que o servidor confere. Aqui só se decide o
 * que aparece: opção que não faz sentido some, e opção que faz sentido mas
 * passou do prazo aparece apagada, dizendo por quê. Sumir sem explicação faria
 * a pessoa achar que o "Editar" quebrou.
 */

const ITENS: {
  acao: AcaoDaMensagem
  rotulo: string
  Icone: (p: { className?: string }) => React.ReactNode
}[] = [
  { acao: 'responder', rotulo: 'Responder', Icone: IconeResponder },
  { acao: 'copiar', rotulo: 'Copiar', Icone: IconeCopiar },
  { acao: 'editar', rotulo: 'Editar', Icone: IconeLapis },
  { acao: 'apagar', rotulo: 'Apagar', Icone: IconeLixeira },
]

export interface OpcaoDoMenu {
  acao: AcaoDaMensagem
  rotulo: string
  Icone: (p: { className?: string }) => React.ReactNode
  bloqueio: string | null
}

/**
 * As opções desta mensagem para esta pessoa, agora.
 *
 * `podeResponder`: a caixa de escrever está disponível (conversa aberta e
 * número que envia). `podeEditar` e `podeApagar`: o número envia e a pessoa
 * tem a permissão de cada uma (ADR 0038); conversa encerrada ainda deixa
 * corrigir um erro.
 */
export function opcoesDaMensagem(
  mensagem: MensagemDto,
  usuarioId: string,
  contexto: {
    podeResponder: boolean
    podeEditar: boolean
    podeApagar: boolean
    /** Na API oficial, editar e apagar aparecem travados (ADR 0033). */
    provedor?: 'evolution' | 'oficial'
  },
): OpcaoDoMenu[] {
  const paraAcao = {
    direcao: mensagem.direcao,
    tipo: mensagem.tipo,
    conteudo: mensagem.conteudo,
    autorId: mensagem.autorId,
    enviadaEm: mensagem.enviadaEm,
    noWhatsapp: mensagem.noWhatsapp,
    apagadaEm: mensagem.apagadaEm,
  }

  return ITENS.flatMap((item) => {
    if (item.acao === 'responder' && !contexto.podeResponder) return []
    if (item.acao === 'editar' && !contexto.podeEditar) return []
    if (item.acao === 'apagar' && !contexto.podeApagar) return []
    const avaliacao = avaliarAcao(item.acao, paraAcao, usuarioId, new Date(), contexto.provedor)
    return avaliacao.disponivel ? [{ ...item, bloqueio: avaliacao.bloqueio }] : []
  })
}

/** Largura do menu, em px: é o `w-56` da lista. */
const LARGURA = 224
const FOLGA = 8

export function MenuDaMensagem({
  opcoes,
  aoEscolher,
  fundo,
}: {
  opcoes: OpcaoDoMenu[]
  aoEscolher: (acao: AcaoDaMensagem) => void
  /** A cor do balão: a setinha fica por cima do texto e precisa cobri-lo. */
  fundo: string
}) {
  const [aberto, setAberto] = useState(false)
  const [posicao, setPosicao] = useState<{ top: number; left: number } | null>(null)
  const botao = useRef<HTMLButtonElement>(null)
  const lista = useRef<HTMLDivElement>(null)

  /*
    Onde o menu abre, medido depois de desenhado.

    O menu vai para o `body`, fora da caixa que rola: dentro dela, o
    `overflow` cortava a lista nas últimas mensagens, justamente as que mais
    se responde. Abre para baixo quando cabe; senão, para cima, colado na
    setinha. E nunca passa das bordas da janela.
  */
  useLayoutEffect(() => {
    if (!aberto) return
    const caixa = botao.current?.getBoundingClientRect()
    const altura = lista.current?.offsetHeight ?? 0
    if (!caixa) return

    const cabeEmbaixo = caixa.bottom + 4 + altura <= window.innerHeight - FOLGA
    const top = cabeEmbaixo ? caixa.bottom + 4 : Math.max(FOLGA, caixa.top - 4 - altura)
    const left = Math.min(
      Math.max(FOLGA, caixa.right - LARGURA),
      window.innerWidth - LARGURA - FOLGA,
    )
    setPosicao({ top, left })
  }, [aberto])

  // Fecha ao clicar fora, no Esc, e ao rolar: o menu fica preso à janela, e
  // rolando a conversa ele ficaria para trás, apontando para outra mensagem.
  useEffect(() => {
    if (!aberto) return
    const fechar = () => setAberto(false)
    const noMouseDown = (evento: MouseEvent) => {
      const alvo = evento.target
      if (!(alvo instanceof Node)) return
      if (botao.current?.contains(alvo) || lista.current?.contains(alvo)) return
      fechar()
    }
    const noTeclado = (evento: KeyboardEvent) => {
      if (evento.key !== 'Escape') return
      fechar()
      botao.current?.focus()
    }
    document.addEventListener('mousedown', noMouseDown)
    document.addEventListener('keydown', noTeclado)
    window.addEventListener('scroll', fechar, true)
    window.addEventListener('resize', fechar)
    return () => {
      document.removeEventListener('mousedown', noMouseDown)
      document.removeEventListener('keydown', noTeclado)
      window.removeEventListener('scroll', fechar, true)
      window.removeEventListener('resize', fechar)
    }
  }, [aberto])

  if (opcoes.length === 0) return null

  return (
    <>
      {/*
        Aparece ao passar o mouse no balão, como no WhatsApp Web: fixa em
        todos, ela tamparia o começo de cada mensagem. Em tela de toque, onde
        não existe passar o mouse, fica sempre à vista.
      */}
      <button
        ref={botao}
        type="button"
        aria-label="Opções da mensagem"
        title="Opções da mensagem"
        aria-haspopup="menu"
        aria-expanded={aberto}
        onClick={() => {
          setPosicao(null)
          setAberto((a) => !a)
        }}
        className={juntar(
          'absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full text-tinta-500 transition hover:text-tinta-900 focus-visible:opacity-100 [@media(hover:none)]:opacity-100',
          fundo,
          aberto ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
        )}
      >
        <IconeSetaBaixo className="h-4 w-4" />
      </button>

      {aberto
        ? createPortal(
            <div
              ref={lista}
              role="menu"
              style={{
                top: posicao?.top ?? 0,
                left: posicao?.left ?? 0,
                // Invisível no primeiro quadro, enquanto a posição é medida.
                visibility: posicao ? 'visible' : 'hidden',
              }}
              className="fixed z-50 w-56 rounded-xl bg-superficie p-1 shadow-media ring-1 ring-tinta-200"
            >
              {opcoes.map(({ acao, rotulo, Icone, bloqueio }) => (
                <button
                  key={acao}
                  type="button"
                  role="menuitem"
                  disabled={Boolean(bloqueio)}
                  title={bloqueio ?? undefined}
                  onClick={() => {
                    setAberto(false)
                    aoEscolher(acao)
                  }}
                  className={juntar(
                    'flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition',
                    bloqueio
                      ? 'cursor-not-allowed text-tinta-400'
                      : acao === 'apagar'
                        ? 'text-red-700 hover:bg-red-50'
                        : 'text-tinta-900 hover:bg-tinta-50',
                  )}
                >
                  <Icone className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="min-w-0">
                    {rotulo}
                    {bloqueio ? (
                      <span className="mt-0.5 block text-xs leading-snug text-tinta-400">
                        {bloqueio}
                      </span>
                    ) : null}
                  </span>
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

/**
 * Copia para a área de transferência.
 *
 * `navigator.clipboard` só existe em contexto seguro, e a equipe abre o CRM
 * pelo IP da rede local, em http. Ali o jeito antigo, por um campo escondido,
 * é o que funciona.
 */
export async function copiarTexto(texto: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(texto)
      return true
    }
  } catch {
    // Cai no jeito antigo abaixo.
  }
  const campo = document.createElement('textarea')
  campo.value = texto
  campo.setAttribute('readonly', '')
  campo.style.position = 'fixed'
  campo.style.opacity = '0'
  document.body.appendChild(campo)
  campo.select()
  try {
    return document.execCommand('copy')
  } finally {
    document.body.removeChild(campo)
  }
}
